import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import type { DailyStaffingDayView } from '../../../../services/LaborManagementService/types/laborManagementTypes';
import { formatDayDate } from './dayDate';

/**
 * Rooms and trays allow up to two decimal places and must be non-negative. Anything else is
 * rejected as it is typed — otherwise `-1` reached the payload and the save failed server-side
 * with a 400 (FR-007, FR-015). This is a text field rather than a number field because a number
 * input reports an empty string for the intermediate state `2.`, so the decimal point could never
 * reach the draft; the pattern below permits that trailing dot while still bounding the fraction.
 */
const isEnteredCountValid = (value: string) => /^\d*\.?\d{0,2}$/.test(value);

interface DailyInputEdits {
  roomsEdits: Record<number, string>;
  onRoomsChange: (dayOfWeek: number, value: string) => void;
  traysEdits: Record<number, string>;
  onTraysChange: (dayOfWeek: number, value: string) => void;
}

interface DailyInputsSectionProps {
  days: DailyStaffingDayView[];
  columnTemplate: string;
  dayPairTemplate: string;
  disabled: boolean;
  edits: DailyInputEdits;
}

interface DailyInputRowProps {
  label: string;
  fieldName: string;
  days: DailyStaffingDayView[];
  columnTemplate: string;
  dayPairTemplate: string;
  disabled: boolean;
  valueFor: (day: DailyStaffingDayView) => string;
  /** Shown when the field is empty: the value the server actually used for this day. */
  placeholderFor: (day: DailyStaffingDayView) => string;
  onValueChange: (dayOfWeek: number, value: string) => void;
  /** Last row of the Daily Inputs group - carries the divider off the department rows below. */
  isGroupEnd?: boolean;
}

const DailyInputRow: React.FC<DailyInputRowProps> = ({
  label, fieldName, days, columnTemplate, dayPairTemplate, disabled,
  valueFor, placeholderFor, onValueChange, isGroupEnd = false,
}) => (
  <Box
    data-testid={`daily-input-${fieldName}-row`}
    sx={{
      display: 'grid',
      gridTemplateColumns: columnTemplate,
      gap: 0.5,
      py: 0.55,
      // Operational inputs are a different kind of value from the department staffing rows, so the
      // group closes with a heavier rule instead of the faint between-rows line.
      borderBottom: isGroupEnd ? '2px solid #cbd5e1' : '1px solid #f1f5f9',
      alignItems: 'center',
      backgroundColor: '#f8fafc',
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
      {label}
    </Typography>

    {days.map((day, index) => {
      // Mirror the department rows: the input occupies the Actual column only, so it
      // lines up with the fields below instead of spanning the whole day pair.
      const isLastDay = index === days.length - 1;

      return (
        <Box
          key={`daily-input-${fieldName}-day-${day.dayOfWeek}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: dayPairTemplate,
            columnGap: 0,
            alignItems: 'center',
            borderRight: isLastDay ? 'none' : '1px solid #f1f5f9',
            pr: isLastDay ? 0 : 0.5,
          }}
        >
          <Box />
          <TextField
            size="small"
            type="text"
            value={valueFor(day)}
            onChange={(e) => {
              if (isEnteredCountValid(e.target.value)) onValueChange(day.dayOfWeek, e.target.value);
            }}
            // Select the whole value on focus so typing overwrites the count instead of appending
            // at the click position (same as the Coverage tab's NumberField).
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            placeholder={placeholderFor(day)}
            inputProps={{
              min: 0,
              step: 0.01,
              inputMode: 'decimal',
              'data-testid': `daily-input-${fieldName}-${day.dayOfWeek}`,
              'aria-label': `${label} for ${day.dayLabel} ${formatDayDate(day.date)}`,
            }}
            sx={{
              borderLeft: '1px solid #e8edf5',
              pl: 0.5,
              borderRadius: 1,
              '& .MuiInputBase-root': { height: 30 },
              '& input': { textAlign: 'right', py: 0.25, px: 0.75, fontSize: '0.75rem' },
              // Italic + faded so a derived fallback is never mistaken for an entered value.
              '& input::placeholder': { fontStyle: 'italic', opacity: 0.75 },
            }}
          />
        </Box>
      );
    })}
  </Box>
);

const DailyInputsSection: React.FC<DailyInputsSectionProps> = ({ days, columnTemplate, dayPairTemplate, disabled, edits }) => {
  const showTrays = days.some((day) => day.trays !== null);

  return (
    <>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', pt: 0.75 }}
      >
        Daily Inputs
      </Typography>

      <DailyInputRow
        label="ORs Running"
        fieldName="rooms"
        days={days}
        columnTemplate={columnTemplate}
        dayPairTemplate={dayPairTemplate}
        disabled={disabled}
        valueFor={(day) => edits.roomsEdits[day.dayOfWeek] ?? (day.roomsEntered ? String(day.rooms) : '')}
        // With no entry saved, day.rooms *is* what the server resolved: the weekly budget rooms on
        // a day with cases, 0 on a day without. Surface it so an empty field isn't a mystery.
        placeholderFor={(day) => (day.roomsEntered ? '' : String(day.rooms))}
        onValueChange={edits.onRoomsChange}
        isGroupEnd={!showTrays}
      />

      {showTrays && (
        <DailyInputRow
          label="Trays"
          fieldName="trays"
          days={days}
          columnTemplate={columnTemplate}
          dayPairTemplate={dayPairTemplate}
          disabled={disabled}
          valueFor={(day) => edits.traysEdits[day.dayOfWeek] ?? (day.traysEntered ? String(day.trays) : '')}
          // Same idea: the derived rooms × Default Trays per Room the calc fell back to.
          placeholderFor={(day) => (
            day.traysEntered || day.trays === null ? '' : String(day.trays)
          )}
          onValueChange={edits.onTraysChange}
          isGroupEnd
        />
      )}
    </>
  );
};

export default DailyInputsSection;
